import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { trendyolMpApi } from '../../../config/apiClient';
import GuideButton from '../../common/guide/GuideButton';

interface TrendyolWebhookSettingsModalProps {
  open: boolean;
  onClose: () => void;
  integrationLabel?: string;
}

interface TrendyolWebhookItem {
  id: string;
  url: string;
  isActive: boolean;
  eventTypes: string[];
}

interface WebhookFormState {
  url: string;
  isActive: boolean;
  eventTypes: string[];
}

const EVENT_TYPE_OPTIONS = [
  { value: 'Created', label: 'Sipariş oluştu' },
  { value: 'Picking', label: 'Hazırlanıyor (Picking)' },
  { value: 'Invoiced', label: 'Faturalandı (Invoiced)' },
  { value: 'Shipped', label: 'Kargoya verildi (Shipped)' },
  { value: 'Delivered', label: 'Teslim edildi (Delivered)' },
  { value: 'Cancelled', label: 'İptal edildi (Cancelled)' },
  { value: 'UnPacked', label: 'Paketlenmedi (UnPacked)' },
  { value: 'UnSupplied', label: 'Tedarik edilemedi (UnSupplied)' },
  { value: 'Returned', label: 'İade edildi (Returned)' }
];

const STATUS_CANONICAL_MAP: Record<string, string> = {
  CREATED: 'Created',
  PICKING: 'Picking',
  INVOICED: 'Invoiced',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  CANCELED: 'Cancelled',
  UNPACKED: 'UnPacked',
  UNSUPPLIED: 'UnSupplied',
  UN_SUPPLIED: 'UnSupplied',
  RETURNED: 'Returned'
};

const DEFAULT_FORM: WebhookFormState = {
  url: '',
  isActive: true,
  eventTypes: []
};

const WEBHOOK_AUTH_USERNAME = 'gts-webhook-user';
const WEBHOOK_AUTH_PASSWORD = 'gts-webhook-pass';

const LOCAL_TRENDYOL_API_BASE_URL = 'http://localhost:5018';
const CAN_USE_LOCAL_FALLBACK =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const shouldUseLocalFallback = (err: any): boolean => {
  if (!CAN_USE_LOCAL_FALLBACK) return false;
  const status = err?.response?.status;
  const message = String(err?.message || '');
  return status === 404 || err?.code === 'ERR_NETWORK' || message.includes('Network Error');
};

const normalizeToArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map(v => (typeof v === 'string' ? v.trim() : String(v ?? '').trim()))
      .filter(Boolean);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeStatusForTrendyol = (status: string): string => {
  return String(status || '').trim().toUpperCase().replace('_', '');
};

const normalizeStatusForUi = (status: string): string => {
  const normalized = String(status || '').trim().toUpperCase();
  return STATUS_CANONICAL_MAP[normalized] ?? status.trim();
};

const extractWebhookList = (raw: any): any[] => {
  const data = raw?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(raw?.data?.content)) return raw.data.content;
  if (Array.isArray(raw?.content)) return raw.content;
  return [];
};

const pickPreferredWebhook = (items: TrendyolWebhookItem[], preferredUrl?: string): TrendyolWebhookItem | null => {
  if (!items.length) return null;

  const normalizedPreferredUrl = (preferredUrl || '').trim().toLowerCase();
  if (normalizedPreferredUrl) {
    const exactMatch = items.find(w => w.url.trim().toLowerCase() === normalizedPreferredUrl);
    if (exactMatch) return exactMatch;
  }

  const activeNewWebhook = items.find(
    w =>
      w.isActive &&
      (w.url.toLowerCase().includes('/api/order-webhook') ||
        w.url.toLowerCase().includes('/api/trendyolwebhook/order'))
  );
  if (activeNewWebhook) return activeNewWebhook;

  const anyActive = items.find(w => w.isActive);
  if (anyActive) return anyActive;

  return items[0];
};

const buildUiErrorMessage = (err: any, fallbackMessage: string): string => {
  const status = err?.response?.status;
  const responseData = err?.response?.data;
  const apiErrors = Array.isArray(responseData?.data?.errors) ? responseData.data.errors : [];
  const firstApiError =
    apiErrors.find((e: any) => typeof e?.message === 'string' && e.message.trim().length > 0)?.message ||
    apiErrors.find((e: any) => typeof e?.key === 'string' && e.key.trim().length > 0)?.key;
  const messageFromBody =
    firstApiError ||
    responseData?.message ||
    responseData?.Message ||
    responseData?.error ||
    responseData?.raw ||
    responseData?.data?.message ||
    responseData?.data?.Message ||
    responseData?.data?.error ||
    responseData?.data?.raw;

  if (status === 404) {
    return "Bildirim endpoint'i uzak Trendyol API servisinde bulunamadı (404). Deploy edilmemiş olabilir.";
  }

  if (err?.code === 'ERR_NETWORK') {
    return "Ağ hatası: Trendyol API alan adı çözümlenemedi veya erişilemiyor. DNS/erişim kontrol edin.";
  }

  if (status === 401) {
    const normalized = typeof messageFromBody === 'string' ? messageFromBody.toLowerCase() : '';
    if (normalized.includes('basic authorization failed') || normalized.includes('basic.authorization.failed')) {
      return 'Trendyol Basic Auth hatası (401): API key/apiSecret/sellerId kombinasyonu geçersiz.';
    }
    return 'Trendyol kimlik doğrulama hatası (401). API key/secret ve sellerId bilgilerini kontrol edin.';
  }

  if (status === 400 && typeof messageFromBody === 'string' && messageFromBody.trim().length > 0) {
    return messageFromBody;
  }

  if (typeof messageFromBody === 'string' && messageFromBody.trim().length > 0) {
    return `${fallbackMessage} (${messageFromBody})`;
  }

  return fallbackMessage;
};

const isValidWebhookId = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const id = value.trim();
  return id.length > 0 && id.toLowerCase() !== 'null' && id.toLowerCase() !== 'undefined';
};

const normalizeUrlKey = (value: string): string => value.trim().replace(/\/+$/, '').toLowerCase();

const toNormalizedWebhook = (raw: any): TrendyolWebhookItem => {
  const id = String(raw?.webhookId ?? raw?.id ?? raw?.Id ?? '');
  const url = String(raw?.url ?? raw?.address ?? raw?.callbackUrl ?? raw?.endpoint ?? '').trim();
  const statusText = String(raw?.status ?? '').toLowerCase();
  const isActive =
    Boolean(raw?.isActive ?? raw?.active ?? raw?.enabled) ||
    statusText === 'active' ||
    statusText === 'activated';
  const eventTypes = Array.from(
    new Set(
      normalizeToArray(
        raw?.eventTypes ?? raw?.events ?? raw?.eventType ?? raw?.statusFilters ?? raw?.statuses ?? raw?.subscribedStatuses
      )
        .map(normalizeStatusForUi)
        .filter(Boolean)
    )
  );

  return { id, url, isActive, eventTypes };
};

const TrendyolWebhookSettingsModal: React.FC<TrendyolWebhookSettingsModalProps> = ({
  open,
  onClose,
  integrationLabel = 'Trendyol'
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [webhooks, setWebhooks] = useState<TrendyolWebhookItem[]>([]);
  const [form, setForm] = useState<WebhookFormState>(DEFAULT_FORM);

  const trendyolGetWithLocalFallback = async (url: string) => {
    try {
      return await trendyolMpApi.get(url);
    } catch (err: any) {
      if (shouldUseLocalFallback(err)) {
        return await trendyolMpApi.request({
          method: 'GET',
          url,
          baseURL: LOCAL_TRENDYOL_API_BASE_URL
        });
      }
      throw err;
    }
  };

  const trendyolPostWithLocalFallback = async (url: string, data?: any) => {
    try {
      return await trendyolMpApi.post(url, data);
    } catch (err: any) {
      if (shouldUseLocalFallback(err)) {
        return await trendyolMpApi.request({
          method: 'POST',
          url,
          data,
          baseURL: LOCAL_TRENDYOL_API_BASE_URL
        });
      }
      throw err;
    }
  };

  const loadWebhooks = async (preferredUrl?: string): Promise<TrendyolWebhookItem[]> => {
    setLoading(true);
    setError(null);
    setInfoMessage(null);

    try {
      const response = await trendyolGetWithLocalFallback('/api/TrendyolWebhookManagement/list');
      const rawList = extractWebhookList(response?.data);
      const normalized = rawList
        .map(toNormalizedWebhook)
        .filter((w: TrendyolWebhookItem) => w.id.length > 0 || w.url.length > 0);

      setWebhooks(normalized);

      if (normalized.length > 0) {
        const selected = pickPreferredWebhook(normalized, preferredUrl) ?? normalized[0];
        setForm({
          url: selected.url,
          isActive: selected.isActive,
          eventTypes: selected.eventTypes
        });
      } else {
        setForm(DEFAULT_FORM);
      }
      return normalized;
    } catch (err: any) {
      console.error('Webhook listesi alınamadı:', err);
      const message = buildUiErrorMessage(err, 'Webhook listesi alınırken hata oluştu.');
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadWebhooks();
    } else {
      setError(null);
      setInfoMessage(null);
      setSaving(false);
      setLoading(false);
    }
  }, [open]);

  const handleEventTypesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setForm(prev => ({
      ...prev,
      eventTypes: Array.from(
        new Set(
          (Array.isArray(value) ? value : String(value).split(','))
            .map(v => normalizeStatusForUi(v.trim()))
            .filter(Boolean)
        )
      )
    }));
  };

  const validateForm = (): string | null => {
    const url = form.url.trim();
    if (!url) return 'Bildirim adresi zorunludur.';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return 'Bildirim adresi http:// veya https:// ile başlamalıdır.';
    }
    return null;
  };

const buildPayload = () => {
  const subscribedStatuses = Array.from(
    new Set(form.eventTypes.map(normalizeStatusForTrendyol).filter(Boolean))
  );
  return {
    url: form.url.trim(),
    authenticationType: 'BASIC_AUTHENTICATION',
    username: WEBHOOK_AUTH_USERNAME,
    password: WEBHOOK_AUTH_PASSWORD,
    subscribedStatuses,

    // Backward compatibility with older internal payload mapping.
    eventTypes: form.eventTypes,
    statusFilters: form.eventTypes
  };
};

  const ensureActiveState = async (webhookId: string, shouldBeActive: boolean) => {
    if (!webhookId) return;
    const endpoint = shouldBeActive ? '/api/TrendyolWebhookManagement/activate' : '/api/TrendyolWebhookManagement/deactivate';
    await trendyolPostWithLocalFallback(endpoint, { webhookId });
  };

  const saveSettings = async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);
    setError(null);
    setInfoMessage(null);

    try {
      const payload = buildPayload();
      const payloadUrlKey = normalizeUrlKey(payload.url);
      const matchedByUrl = webhooks.find(
        w => isValidWebhookId(w.id) && normalizeUrlKey(w.url) === payloadUrlKey
      );
      let targetWebhookId = matchedByUrl?.id ?? '';

      if (!isValidWebhookId(targetWebhookId)) {
        await trendyolPostWithLocalFallback('/api/TrendyolWebhookManagement/create', payload);
        const refreshed = await loadWebhooks();
        const createdWebhook = refreshed.find(
          w => isValidWebhookId(w.id) && normalizeUrlKey(w.url) === payloadUrlKey
        );
        targetWebhookId = createdWebhook?.id ?? '';
      } else {
        try {
          await trendyolPostWithLocalFallback('/api/TrendyolWebhookManagement/update', {
            webhookId: targetWebhookId,
            payload
          });
        } catch (updateErr: any) {
          const status = updateErr?.response?.status;
          const updateMessage =
            updateErr?.response?.data?.message ||
            updateErr?.response?.data?.data?.message ||
            updateErr?.message ||
            '';
          const normalizedMessage = String(updateMessage).toLowerCase();
          const shouldFallbackToCreate =
            status === 400 ||
            status === 404 ||
            normalizedMessage.includes('webhookid') ||
            normalizedMessage.includes('invalid webhook') ||
            normalizedMessage.includes('not found');

          if (!shouldFallbackToCreate) {
            throw updateErr;
          }

          await trendyolPostWithLocalFallback('/api/TrendyolWebhookManagement/create', payload);
          const refreshed = await loadWebhooks();
          const createdWebhook = refreshed.find(
            w => isValidWebhookId(w.id) && normalizeUrlKey(w.url) === payloadUrlKey
          );
          targetWebhookId = createdWebhook?.id ?? '';
        }
      }

      if (isValidWebhookId(targetWebhookId)) {
        await ensureActiveState(targetWebhookId, form.isActive);

        if (form.isActive) {
          // Keep the selected URL active and passivate other active webhooks.
          const othersToDeactivate = webhooks
            .filter(w => isValidWebhookId(w.id) && w.id !== targetWebhookId && w.isActive)
            .map(w => w.id);

          for (const otherId of othersToDeactivate) {
            try {
              await ensureActiveState(otherId, false);
            } catch {
              // Best effort; don't block save success if one deactivation fails.
            }
          }
        }
      }

      await loadWebhooks(payload.url);
      setInfoMessage('Bildirim ayarları kaydedildi.');
    } catch (err: any) {
      console.error('Webhook kaydı başarısız:', err);
      const message = buildUiErrorMessage(err, 'Bildirim ayarları kaydedilemedi.');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {integrationLabel} - Sipariş Bildirim Ayarları
        <GuideButton guideKey="ecommerce.trendyolWebhook" size="small" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            Buradaki ayarlar Trendyol&apos;dan gelen sipariş güncellemeleri içindir (Trendyol - GTS).
          </Alert>

          {loading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                label="Bildirim adresi"
                fullWidth
                value={form.url}
                onChange={e => setForm(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://ornek.com/api/order-webhook"
              />

              <FormControl fullWidth>
                <InputLabel id="status-events-label">Bildirim olayları (durum)</InputLabel>
                <Select
                  labelId="status-events-label"
                  multiple
                  value={form.eventTypes}
                  onChange={handleEventTypesChange}
                  renderValue={(selected) =>
                    selected.length > 0
                      ? selected
                        .map(value => EVENT_TYPE_OPTIONS.find(option => option.value === value)?.label ?? value)
                        .join(', ')
                      : 'Tüm durumlar'
                  }
                  label="Bildirim olayları (durum)"
                >
                  {EVENT_TYPE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={form.eventTypes.includes(option.value)} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">Bildirim aktif mi?</Typography>
                <Switch
                  checked={form.isActive}
                  onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              </Box>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {infoMessage && <Alert severity="success">{infoMessage}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Kapat</Button>
        <Button onClick={() => { void loadWebhooks(); }} disabled={loading || saving}>Yenile</Button>
        <Button onClick={saveSettings} variant="contained" disabled={loading || saving}>
          {saving ? <CircularProgress size={20} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendyolWebhookSettingsModal;
